/**
 * What to upload for each Lite entry. The server loads by file extension only (ApiController.ParseFileNameWithCompression)
 * and has loaders for pdb/ent, mmcif/cif, sdf, smi, xyz, pqr, pdbqt, gro, mae, moe, plus .gz/.maegz; there is no
 * MOL, MOL2, BCIF or MMTF loader (WorkspaceAPI/src/Adapter/IO/Services/*.cs CanLoad; research
 * open-in-nanome-bridge__07.md "Formats"). So:
 *  - supported extensions pass through untouched (the server re-parses the original bytes)
 *  - .mol / .sd are MDL molfiles: renamed to .sdf, with the '$$$$' record terminator appended when missing
 *  - .mol2 is rewritten as SDF from the parsed NanomeModel (V2000; V3000 above 999 atoms or bonds)
 *  - .bcif cannot be converted here (no CIF writer in this package): the pipeline re-fetches
 *    https://files.rcsb.org/download/{ID}.cif when the entry came from an RCSB id (docs/PLAN.md "Pre-flight dialog")
 *
 * SDF rules the server applies (LoadSdfService.cs:172-229; Ctab2000Parser.cs:60-115, 152-165, 244-256):
 * bond type 4 is aromatic (stored as Single + aromatic flag), charges come from `M  CHG` (and the legacy atom-line
 * charge field, mapped 1..7 -> +3..-3), atom serial = position in the record, model serial = record index.
 */
import type { Entry } from '@nanome-lite/state';
import type { NanomeModel } from '@nanome-lite/data';

export interface PreparedUpload {
  /** File name to send as the multipart part's filename; the server names the entry after it, up to its last dot. */
  name: string;
  bytes: Uint8Array;
  /** True when the bytes differ from the entry's original bytes. */
  converted: boolean;
  /** Set when nothing could be done here (bcif, unknown extension): the entry cannot upload as it is. */
  note?: string;
  /** For .bcif entries loaded from RCSB: the mmCIF URL the pipeline fetches instead. */
  fallbackUrl?: string;
}

/** Extensions the Workspace API loads as structures (research __07.md). smi/mae/moe/pse are not Lite inputs. */
export const SERVER_STRUCTURE_EXTENSIONS: ReadonlySet<string> = new Set(['pdb', 'ent', 'cif', 'mmcif', 'sdf', 'xyz', 'gro', 'pdbqt', 'pqr', 'smi', 'smiles', 'mae', 'moe']);

export const RCSB_CIF_URL = (id: string) => `https://files.rcsb.org/download/${id.toUpperCase()}.cif`;

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

/** Replace (or add) the extension of a file name; keeps a trailing .gz out of the way. */
export function withExtension(fileName: string, ext: string): string {
  const base = fileName.replace(/\.gz$/i, '').replace(/\.[^./\\]*$/, '');
  return `${base}.${ext}`;
}

const looksLikeRcsbId = (s: string | undefined): s is string => !!s && /^[0-9][A-Za-z0-9]{3}$/.test(s);

/** Decide the upload for one entry. Pure and synchronous; the pipeline handles fallbackUrl fetches. */
export function prepareUpload(entry: Entry, model: NanomeModel | undefined): PreparedUpload {
  const src = entry.source;
  const ext = src.ext.toLowerCase();
  const name = src.fileName;
  if (SERVER_STRUCTURE_EXTENSIONS.has(ext)) return { name, bytes: src.bytes, converted: false };

  if (ext === 'mol' || ext === 'sd') {
    if (src.gz) {
      if (!model) return { name, bytes: src.bytes, converted: false, note: `${name} is a compressed MDL molfile and no parsed model is available to rewrite it as SDF.` };
      return { name: withExtension(name, 'sdf'), bytes: textEncoder.encode(writeSdf(model, { title: entry.name })), converted: true, note: `${name} rewritten as SDF from the parsed model.` };
    }
    const text = textDecoder.decode(src.bytes);
    return { name: withExtension(name, 'sdf'), bytes: textEncoder.encode(molToSdf(text)), converted: true, note: `${name} renamed to .sdf (the server has no .${ext} loader).` };
  }

  if (ext === 'mol2') {
    if (!model) return { name, bytes: src.bytes, converted: false, note: `${name} is a MOL2 file and no parsed model is available to rewrite it as SDF.` };
    return { name: withExtension(name, 'sdf'), bytes: textEncoder.encode(writeSdf(model, { title: entry.name })), converted: true, note: `${name} rewritten as SDF from the parsed model (the server has no MOL2 loader).` };
  }

  if (ext === 'bcif') {
    const out: PreparedUpload = { name, bytes: src.bytes, converted: false, note: `${name} is BinaryCIF, which the server cannot load and this package cannot convert.` };
    const ref = src.ref ?? (looksLikeRcsbId(entry.name) ? entry.name : undefined);
    if (looksLikeRcsbId(ref)) {
      out.fallbackUrl = RCSB_CIF_URL(ref);
      out.note = `${name} is BinaryCIF; the mmCIF file for ${ref.toUpperCase()} is fetched from RCSB instead.`;
    }
    return out;
  }

  return { name, bytes: src.bytes, converted: false, note: `${name}: the server has no loader for .${ext}.` };
}

/** An MDL molfile is one SDF record without the '$$$$' terminator; add it (and a newline) when missing. */
export function molToSdf(text: string): string {
  const normalized = text.replace(/\r\n?/g, '\n');
  const trimmed = normalized.replace(/\s+$/, '');
  if (/\n\$\$\$\$$/.test(trimmed) || trimmed === '$$$$') return trimmed + '\n';
  return trimmed + '\n$$$$\n';
}

// --- SDF writer -------------------------------------------------------------------------------------------------------

/** Element symbols by atomic number (index 0 unused; 'X' for unknown). */
export const ELEMENT_SYMBOLS: readonly string[] = ['X',
  'H', 'He', 'Li', 'Be', 'B', 'C', 'N', 'O', 'F', 'Ne', 'Na', 'Mg', 'Al', 'Si', 'P', 'S', 'Cl', 'Ar', 'K', 'Ca',
  'Sc', 'Ti', 'V', 'Cr', 'Mn', 'Fe', 'Co', 'Ni', 'Cu', 'Zn', 'Ga', 'Ge', 'As', 'Se', 'Br', 'Kr', 'Rb', 'Sr', 'Y', 'Zr',
  'Nb', 'Mo', 'Tc', 'Ru', 'Rh', 'Pd', 'Ag', 'Cd', 'In', 'Sn', 'Sb', 'Te', 'I', 'Xe', 'Cs', 'Ba', 'La', 'Ce', 'Pr', 'Nd',
  'Pm', 'Sm', 'Eu', 'Gd', 'Tb', 'Dy', 'Ho', 'Er', 'Tm', 'Yb', 'Lu', 'Hf', 'Ta', 'W', 'Re', 'Os', 'Ir', 'Pt', 'Au', 'Hg',
  'Tl', 'Pb', 'Bi', 'Po', 'At', 'Rn', 'Fr', 'Ra', 'Ac', 'Th', 'Pa', 'U', 'Np', 'Pu', 'Am', 'Cm', 'Bk', 'Cf', 'Es', 'Fm',
  'Md', 'No', 'Lr', 'Rf', 'Db', 'Sg', 'Bh', 'Hs', 'Mt', 'Ds', 'Rg', 'Cn', 'Nh', 'Fl', 'Mc', 'Lv', 'Ts', 'Og'];

export const elementSymbol = (atomicNumber: number): string => ELEMENT_SYMBOLS[atomicNumber] ?? 'X';

export interface WriteSdfOptions {
  /** Record title (line 1). Defaults to ''. */
  title?: string;
  /** Force a CTAB version; default V2000 up to 999 atoms and 999 bonds, else V3000. */
  version?: 'V2000' | 'V3000';
  /** Write every frame as its own record (default) so the server's model serials match Lite's frames. */
  allFrames?: boolean;
}

/** V2000 holds at most 999 atoms and 999 bonds (3-character counts). */
export const V2000_MAX = 999;

/** SDF text from a NanomeModel: one record per frame. Bond order 4 for aromatic bonds, charges as `M  CHG` (V2000) or CHG= (V3000). */
export function writeSdf(model: NanomeModel, options: WriteSdfOptions = {}): string {
  const version = options.version ?? ((model.atomCount > V2000_MAX || model.bonds.count > V2000_MAX) ? 'V3000' : 'V2000');
  const frames = options.allFrames === false ? [model.xyz] : (model.frames.length ? model.frames : [model.xyz]);
  const title = (options.title ?? '').replace(/[\r\n]/g, ' ').slice(0, 80);
  let out = '';
  for (const xyz of frames) out += version === 'V3000' ? writeRecordV3000(model, xyz, title) : writeRecordV2000(model, xyz, title);
  return out;
}

const pad = (s: string, w: number) => s.length >= w ? s : ' '.repeat(w - s.length) + s;
const fmt3 = (n: number) => pad(String(n), 3);
const fmt10 = (x: number) => pad(x.toFixed(4), 10);
const header = (title: string) => `${title}\n  NanomeLite          3D\n\n`;

function bondOrderOf(model: NanomeModel, i: number): number {
  return model.bonds.aromatic[i] ? 4 : Math.min(3, Math.max(1, model.bonds.order[i] ?? 1));
}

function writeRecordV2000(model: NanomeModel, xyz: Float32Array, title: string): string {
  if (model.atomCount > V2000_MAX || model.bonds.count > V2000_MAX) throw new Error(`V2000 holds at most ${V2000_MAX} atoms and bonds; use V3000`);
  const lines: string[] = [];
  lines.push(`${fmt3(model.atomCount)}${fmt3(model.bonds.count)}  0  0  0  0  0  0  0  0999 V2000`);
  for (let i = 0; i < model.atomCount; i++) {
    const sym = elementSymbol(model.element[i]!);
    // x y z symbol mass-diff charge(legacy, 0: charges go to M CHG) stereo hcount stereo-care valence ...
    lines.push(`${fmt10(xyz[3 * i]!)}${fmt10(xyz[3 * i + 1]!)}${fmt10(xyz[3 * i + 2]!)} ${sym.padEnd(3)} 0  0  0  0  0  0  0  0  0  0  0  0`);
  }
  for (let i = 0; i < model.bonds.count; i++) {
    lines.push(`${fmt3(model.bonds.a[i]! + 1)}${fmt3(model.bonds.b[i]! + 1)}${fmt3(bondOrderOf(model, i))}  0  0  0  0`);
  }
  const charged: [number, number][] = [];
  for (let i = 0; i < model.atomCount; i++) if (model.formalCharge[i]) charged.push([i + 1, model.formalCharge[i]!]);
  for (let k = 0; k < charged.length; k += 8) { // at most 8 pairs per M CHG line
    const chunk = charged.slice(k, k + 8);
    lines.push(`M  CHG${fmt3(chunk.length)}${chunk.map(([a, c]) => `${pad(String(a), 4)}${pad(String(c), 4)}`).join('')}`);
  }
  lines.push('M  END', '$$$$');
  return header(title) + lines.join('\n') + '\n';
}

function writeRecordV3000(model: NanomeModel, xyz: Float32Array, title: string): string {
  const lines: string[] = [];
  lines.push('  0  0  0  0  0  0  0  0  0  0999 V3000');
  lines.push('M  V30 BEGIN CTAB');
  lines.push(`M  V30 COUNTS ${model.atomCount} ${model.bonds.count} 0 0 0`);
  lines.push('M  V30 BEGIN ATOM');
  for (let i = 0; i < model.atomCount; i++) {
    const charge = model.formalCharge[i] ? ` CHG=${model.formalCharge[i]}` : '';
    lines.push(`M  V30 ${i + 1} ${elementSymbol(model.element[i]!)} ${xyz[3 * i]!.toFixed(4)} ${xyz[3 * i + 1]!.toFixed(4)} ${xyz[3 * i + 2]!.toFixed(4)} 0${charge}`);
  }
  lines.push('M  V30 END ATOM');
  lines.push('M  V30 BEGIN BOND');
  for (let i = 0; i < model.bonds.count; i++) lines.push(`M  V30 ${i + 1} ${bondOrderOf(model, i)} ${model.bonds.a[i]! + 1} ${model.bonds.b[i]! + 1}`);
  lines.push('M  V30 END BOND');
  lines.push('M  V30 END CTAB');
  lines.push('M  END', '$$$$');
  return header(title) + lines.join('\n') + '\n';
}
